/**
 * „Stand sichern" mit und ohne Belegfoto (Spec 46b, Fachregel 21): Reihenfolge
 * `upload-url` → `PUT` → `POST /documents` (zusammen `useCreateReadingPhoto`),
 * dann `POST …/readings`. Scheitert ein Schritt, bleibt die Eingabe stehen; der
 * Wiederholungsversuch beginnt beim gescheiterten Schritt — ein bereits
 * angelegtes Dokument wird nicht noch einmal hochgeladen, ein gescheiterter
 * `PUT` erreicht `POST /documents` nie.
 *
 * `saveReading` ist die reine Reihenfolge (ohne React, in `useReadingUpload.
 * test.ts` geprüft), der Hook hängt Mutationen und Fehlerzustand daran.
 */

import { useRef, useState } from 'react'

import { useCreateReading, useCreateReadingPhoto } from '@/features/energy/api'
import type { MeterReadingCreate } from '@/features/energy/api'
import { sourceFor } from '@/features/energy/reading-draft'
import type { FieldError } from '@/components/FormField'
import { ApiError } from '@/lib/api'

export interface ReadingSaveInput {
  readonly meterId: string
  readonly value: number
  readonly readOn: string
  /** Das Belegfoto, oder `null` (Quelle `manual`). */
  readonly photo: File | null
}

/** Was zwischen zwei Versuchen stehen bleibt. */
export interface ReadingMemo {
  /** Das Foto, dessen Dokument schon angelegt ist. */
  readonly photoFile: File | null
  readonly photoDocumentId: string | null
  /** Ein Schlüssel je Rumpf: derselbe Rumpf wiederholt denselben Stand nie doppelt. */
  readonly readingBody: string | null
  readonly readingKey: string | null
}

export const EMPTY_MEMO: ReadingMemo = { photoFile: null, photoDocumentId: null, readingBody: null, readingKey: null }

export interface ReadingSaveSteps {
  readonly uploadPhoto: (args: { meterId: string; file: File; readOn: string }) => Promise<string>
  readonly createReading: (args: {
    meterId: string
    body: MeterReadingCreate
    idempotencyKey: string
  }) => Promise<unknown>
}

export async function saveReading(
  steps: ReadingSaveSteps,
  input: ReadingSaveInput,
  memo: { current: ReadingMemo },
): Promise<void> {
  const { meterId, value, readOn, photo } = input

  if (photo !== null && memo.current.photoFile !== photo) {
    const photoDocumentId = await steps.uploadPhoto({ meterId, file: photo, readOn })
    memo.current = { ...memo.current, photoFile: photo, photoDocumentId }
  }

  const photoDocumentId = photo === null ? null : memo.current.photoDocumentId
  const body: MeterReadingCreate = {
    value,
    read_on: readOn,
    source: sourceFor({ photo: photoDocumentId !== null }),
    ...(photoDocumentId === null ? {} : { photo_document_id: photoDocumentId }),
  }
  const serialized = JSON.stringify(body)
  const readingKey = memo.current.readingBody === serialized ? memo.current.readingKey : null
  const idempotencyKey = readingKey ?? crypto.randomUUID()
  memo.current = { ...memo.current, readingBody: serialized, readingKey: idempotencyKey }

  await steps.createReading({ meterId, body, idempotencyKey })
}

export interface ReadingSaveError {
  /** Feldfehler am Ziffernfeld oder am Datum (422). */
  readonly field?: FieldError
  /** Alles andere: 403, 409, 5xx, Netz, Upload. */
  readonly general?: string
}

const CAPTURE_FIELDS = new Set(['value', 'read_on'])

export function toReadingSaveError(error: unknown): ReadingSaveError {
  if (error instanceof ApiError) {
    if (error.status === 422 && error.field !== null && CAPTURE_FIELDS.has(error.field)) {
      return { field: { field: error.field, message: error.message } }
    }
    return { general: error.message }
  }
  return { general: 'Die Verbindung zum Server ist unterbrochen.' }
}

/** Der Einstieg „Zähler wurde getauscht" erscheint nur unter dieser Meldung
 * (Spec 46a, Regel 9: „Kleiner als der letzte Stand …"). */
export function showsReplaceEntry(error: ReadingSaveError): boolean {
  return error.field?.field === 'value' && error.field.message.startsWith('Kleiner als der letzte Stand')
}

export function useReadingUpload() {
  const photoMutation = useCreateReadingPhoto()
  const readingMutation = useCreateReading()
  const memo = useRef<ReadingMemo>(EMPTY_MEMO)
  const [error, setError] = useState<ReadingSaveError>({})
  const [isSaving, setIsSaving] = useState(false)

  async function save(input: ReadingSaveInput): Promise<boolean> {
    setError({})
    setIsSaving(true)
    try {
      await saveReading(
        { uploadPhoto: photoMutation.mutateAsync, createReading: readingMutation.mutateAsync },
        input,
        memo,
      )
      return true
    } catch (caught) {
      setError(toReadingSaveError(caught))
      return false
    } finally {
      setIsSaving(false)
    }
  }

  return { save, error, clearError: () => { setError({}) }, isSaving }
}
